import { useWsStore } from '../store/ws';
import { getToken } from '../auth/session';

const PING_INTERVAL_MS = 5_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

interface PongMessage {
  type: 'pong';
  ts: number;
  server_ts: number;
}

export interface NotifyMessage<T = unknown> {
  type: 'notify';
  topic: string;
  payload: T;
}

interface ErrorMessage {
  type: 'error';
  message: string;
}

type Inbound = PongMessage | NotifyMessage | ErrorMessage;

let socket: WebSocket | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let started = false;

const notifyHandlers = new Set<(msg: NotifyMessage) => void>();
const openHandlers = new Set<() => void>();

export function addNotifyHandler(fn: (msg: NotifyMessage) => void): void {
  notifyHandlers.add(fn);
}
export function removeNotifyHandler(fn: (msg: NotifyMessage) => void): void {
  notifyHandlers.delete(fn);
}
export function addOpenHandler(fn: () => void): void {
  openHandlers.add(fn);
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      fn();
    } catch (err) {
      console.error('open handler threw', err);
    }
  }
}
export function removeOpenHandler(fn: () => void): void {
  openHandlers.delete(fn);
}

function sendFrame(payload: unknown): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  socket.send(JSON.stringify(payload));
  return true;
}

export function sendSubscribe(topic: string): void {
  sendFrame({ type: 'subscribe', topic });
}
export function sendUnsubscribe(topic: string): void {
  sendFrame({ type: 'unsubscribe', topic });
}

function endpoint(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // The /ws upgrade is JWT-gated; browsers can't set headers on a
  // WebSocket, so the token rides as a query param. Read it at (re)connect
  // time so a fresh login is picked up.
  const token = getToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${proto}://${window.location.host}/ws${query}`;
}

function clearPing() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function clearReconnect() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  const delay = Math.min(
    RECONNECT_BASE_MS * 2 ** reconnectAttempt,
    RECONNECT_MAX_MS,
  );
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    open();
  }, delay);
}

function open() {
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }

  useWsStore.getState().setStatus('connecting');
  const ws = new WebSocket(endpoint());
  socket = ws;

  ws.addEventListener('open', () => {
    reconnectAttempt = 0;
    useWsStore.getState().setStatus('open');
    pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
      }
    }, PING_INTERVAL_MS);
    openHandlers.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error('open handler threw', err);
      }
    });
  });

  ws.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    let parsed: Inbound;
    try {
      parsed = JSON.parse(event.data) as Inbound;
    } catch {
      return;
    }
    switch (parsed.type) {
      case 'pong': {
        const now = Date.now();
        const rtt = now - parsed.ts;
        const offset =
          typeof parsed.server_ts === 'number'
            ? parsed.server_ts - (now - rtt / 2)
            : 0;
        useWsStore.getState().recordPong(rtt, Math.round(offset));
        break;
      }
      case 'notify': {
        notifyHandlers.forEach((fn) => {
          try {
            fn(parsed as NotifyMessage);
          } catch (err) {
            console.error('notify handler threw', err);
          }
        });
        break;
      }
      case 'error': {
        console.warn('WS server error:', parsed.message);
        break;
      }
    }
  });

  const onClose = () => {
    clearPing();
    if (socket === ws) socket = null;
    useWsStore.getState().setStatus('closed');
    scheduleReconnect();
  };
  ws.addEventListener('close', onClose);
  ws.addEventListener('error', () => ws.close());
}

export function connect(): void {
  if (started) return;
  started = true;
  open();
}

export function disconnect(): void {
  started = false;
  clearReconnect();
  clearPing();
  if (socket) {
    socket.close();
    socket = null;
  }
  useWsStore.getState().reset();
}
